import React from 'react';
import { FileText, Cpu, Sun } from 'lucide-react';
import type { SiteEvent } from '../../redux/api/eventsApi';
import { formatDate } from '../../utils/dateUtils';
import { SEVERITY_CARD_COLORS, EVENT_TYPE_LABELS, componentChipClass } from '../../constants/eventStyles';

interface EventCardProps {
  event: SiteEvent;
  onClick: (eventId: string) => void;
  components?: string[];
  activeComponent?: string;
  onComponentClick?: (component: string) => void;
  connectedPowerKw?: number | null;
}

const EventCard: React.FC<EventCardProps> = ({ event, onClick, components, activeComponent, onComponentClick, connectedPowerKw }) => {
  const sev = SEVERITY_CARD_COLORS[event.severity];

  return (
    <div
      className="w-full text-left p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]
                 hover:bg-[var(--color-surface-hover)] hover:border-[var(--color-border-secondary)]
                 transition-all duration-150 group"
    >
      <button
        type="button"
        onClick={() => onClick(event.id)}
        className="w-full text-left cursor-pointer"
      >
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h4 className="text-sm font-medium text-[var(--color-text-primary)] line-clamp-1 group-hover:text-[#17c480] transition-colors">
            {event.title}
          </h4>
          <span className={`shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded ${sev.bg} ${sev.text} border ${sev.border}`}>
            {event.severity}
          </span>
        </div>

        <div className="flex items-center gap-2 text-xs text-[var(--color-text-tertiary)] mb-1.5">
          <span>{formatDate(event.occurred_at)}</span>
          <span className="text-[var(--color-border)]">|</span>
          <span className="px-1.5 py-0.5 rounded bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] text-[10px] font-medium">
            {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
          </span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
            event.status === 'resolved' || event.status === 'closed'
              ? 'bg-emerald-500/15 text-emerald-500'
              : event.status === 'in_progress'
                ? 'bg-blue-500/15 text-blue-500'
                : 'bg-gray-500/15 text-gray-500'
          }`}>
            {event.status.replace('_', ' ')}
          </span>
        </div>

        {event.description && (
          <p className="text-xs text-[var(--color-text-tertiary)] line-clamp-2 mb-1.5">
            {event.description}
          </p>
        )}

        <div className="flex items-center gap-3 text-[11px] text-[var(--color-text-muted)]">
          {event.document_id && (
            <span
              className="flex items-center gap-1 hover:text-[#17c480] cursor-pointer transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                const sourcePage = event.meta?.source_page;
                const sourceExcerpt = event.meta?.source_excerpt;
                window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'dataroom' } }));
                window.dispatchEvent(new CustomEvent('openDocumentDetail', {
                  detail: {
                    artefactId: event.document_id,
                    sourceLocation: sourcePage != null ? { page_number: sourcePage, excerpt: sourceExcerpt || null } : null,
                  },
                }));
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('openDocumentDetail', {
                    detail: {
                      artefactId: event.document_id,
                      sourceLocation: sourcePage != null ? { page_number: sourcePage, excerpt: sourceExcerpt || null } : null,
                    },
                  }));
                }, 500);
              }}
            >
              <FileText className="w-3 h-3" />
              {event.meta?.source_page != null ? `Page ${event.meta.source_page}` : 'Source doc'}
            </span>
          )}
          {connectedPowerKw != null && (
            <span className="flex items-center gap-1">
              <Sun className="w-3 h-3 text-[#17c480]" />
              {connectedPowerKw.toLocaleString(undefined, { maximumFractionDigits: 1 })} kW
            </span>
          )}
          {event.energy_impact_kwh != null && (
            <span>{event.energy_impact_kwh.toLocaleString()} kWh impact</span>
          )}
        </div>
      </button>

      {components && components.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2 pt-2 border-t border-[var(--color-border)]/50">
          <Cpu className="w-3 h-3 text-[var(--color-text-muted)] shrink-0" />
          {components.map((comp, idx) => (
            <button
              key={`${comp}-${idx}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onComponentClick?.(comp);
              }}
              className={componentChipClass(activeComponent === comp)}
              title={`Filter by ${comp}`}
            >
              {comp}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventCard;
